/**
 * HTTP adapter for the newsletter endpoints. The contract is the Owner's brief (2026-10-11); if the deployed server differs,
 * this is the only file that changes.
 *
 *   POST  /api/newsletter/subscribe    {email, utility, zip, frequency, consent, form_version, turnstile_token}
 *                                      -> 202 {status:"subscribed"}   (identical for new and existing addresses)
 *   POST  /api/newsletter/subscribe also carries offers_consent (the optional offers box) and placement (which page the form was on).
 *   POST  /api/newsletter/preferences/view {token} -> 200 {status, frequency, paused_until, offers:{available, enabled}}
 *   POST  /api/newsletter/confirm      {token}       -> 200 {status:"confirmed", frequency?}
 *   POST  /api/newsletter/unsubscribe  {token}       -> 200 {status:"unsubscribed"}
 *   PATCH /api/newsletter/preferences  {token, frequency?: weekly|biweekly|monthly, pause_days?: integer 1-90 (0 or null resumes)} -> 200
 *
 * SECURITY (CISO M3/M4/M5): every request that changes state is a POST/PATCH with the token in the JSON BODY. A GET never
 * confirms or unsubscribes anyone (the server answers 405), so a mail scanner or link prefetch cannot act, and the token is
 * never in a request URL, so it is never in an access log. The browser address bar of the PAGE still carries ?token=
 * (that is the emailed link); the pages send no referrer.
 *
 * Token errors: HTTP 410 or code TOKEN_EXPIRED = expired; any other 4xx (400/401/403/404) = invalid.
 * Subscribe errors: 429 = rate limited; code TURNSTILE_FAILED (400/403/422) = bot check failed; 400/422 otherwise =
 * validation; 5xx, timeout or no response = unavailable / unknown outcome.
 * The admin token is never attached to these calls (see the interceptor in lib/api.js).
 */
import { api } from "@/lib/api";
import { NEWSLETTER_FORM_VERSION } from "@/lib/newsletterConfig";

const TIMEOUT_MS = 15000;

export class NewsletterApiError extends Error {
  /**
   * kind: "outdated" (form version retired/unknown: reload the page) | "rate_limited" | "turnstile" | "validation" | "expired" | "invalid" | "unsubscribed" | "not_confirmed" | "unavailable" | "unknown_outcome" | "rejected"
   *  - unavailable:     the server answered 5xx; it did not accept the request.
   *  - unknown_outcome: timeout or no response at all; the server may or may not have acted.
   */
  constructor(kind, message, extra = {}) {
    super(message);
    this.name = "NewsletterApiError";
    this.kind = kind;
    this.retryAfterSeconds = extra.retryAfterSeconds || null;
    this.status = extra.status || null;
  }
}

function codeOf(e) {
  const d = e && e.response && e.response.data;
  const raw = d && (d.code || (d.detail && d.detail.code) || (typeof d.detail === "string" ? d.detail : null));
  return typeof raw === "string" ? raw.toUpperCase() : null;
}

function toApiError(e, { token = false } = {}) {
  if (e instanceof NewsletterApiError) return e;
  const status = e && e.response && e.response.status;
  if (!status) return new NewsletterApiError("unknown_outcome", "No response", {});
  const code = codeOf(e);
  if (status === 429) {
    const ra = Number(e.response.headers && e.response.headers["retry-after"]);
    return new NewsletterApiError("rate_limited", "Rate limited", { status, retryAfterSeconds: Number.isFinite(ra) && ra > 0 ? ra : null });
  }
  if (status >= 500) return new NewsletterApiError("unavailable", `HTTP ${status}`, { status });
  if (token) {
    if (code === "OFFERS_NOT_AVAILABLE") return new NewsletterApiError("offers_unavailable", "Offers not available", { status });
    if (code === "UNSUBSCRIBED") return new NewsletterApiError("unsubscribed", "Already unsubscribed", { status });
    if (code === "NOT_CONFIRMED") return new NewsletterApiError("not_confirmed", "Not confirmed yet", { status });
    if (status === 410 || code === "TOKEN_EXPIRED") return new NewsletterApiError("expired", "Expired", { status });
    return new NewsletterApiError("invalid", "Invalid", { status });
  }
  if (code && code.startsWith("TURNSTILE")) return new NewsletterApiError("turnstile", "Turnstile failed", { status });
  if (code === "FORM_VERSION_RETIRED" || code === "UNKNOWN_FORM_VERSION") return new NewsletterApiError("outdated", "Form out of date", { status });
  if (status === 400 || status === 422) return new NewsletterApiError("validation", code || `HTTP ${status}`, { status });
  return new NewsletterApiError("rejected", `HTTP ${status}`, { status });
}

/** Resolves on any 2xx. The body is never used to tell a new address from an existing one (no enumeration). */
export async function subscribeToNewsletter({ email, utility, zip, frequency, consent, offersConsent = false, shareConsent = false,
  placement = null, turnstileToken }) {
  try {
    await api.post(
      "/newsletter/subscribe",
      {
        email,
        // The server requires a utility code and a string token: a blank optional utility is "Not Sure", a missing
        // Turnstile token is "" (the server then answers TURNSTILE_FAILED or accepts it only where the check is off).
        utility: utility || "not_sure",
        zip: zip || null,
        frequency,
        consent: consent === true,                    // the REQUIRED newsletter box
        offers_consent: offersConsent === true,       // the OPTIONAL offers box (never ticked for the visitor)
        // The partner-sharing box is HELD: the form never renders it, so this is only ever sent if a future version turns it on.
        ...(shareConsent === true ? { share_consent: true } : {}),
        ...(placement ? { placement } : {}),
        form_version: NEWSLETTER_FORM_VERSION,
        turnstile_token: turnstileToken || "",
      },
      { timeout: TIMEOUT_MS }
    );
    return { status: "subscribed" };
  } catch (e) {
    throw toApiError(e);
  }
}

export async function confirmSubscription(token) {
  try {
    const r = await api.post("/newsletter/confirm", { token }, { timeout: TIMEOUT_MS });
    return { frequency: (r.data && r.data.frequency) || null };
  } catch (e) {
    throw toApiError(e, { token: true });
  }
}

/** Current preferences for a token page (never the address). offers.available is false for people who signed up under the first wording. */
export async function viewNewsletterPreferences(token) {
  try {
    const r = await api.post("/newsletter/preferences/view", { token }, { timeout: TIMEOUT_MS });
    const d = r.data || {};
    const offers = d.offers || {};
    return { status: d.status || null, frequency: d.frequency || null, pausedUntil: d.paused_until || null,
      offers: { available: offers.available === true, enabled: offers.enabled === true } };
  } catch (e) {
    throw toApiError(e, { token: true });
  }
}

export async function unsubscribeFromNewsletter(token) {
  try {
    // Token in the JSON body (never in the URL, so never in an access log). The server also accepts ?token= because the
    // RFC 8058 List-Unsubscribe URL in the email must carry it there; this page does not use that form.
    await api.post("/newsletter/unsubscribe", { token }, { timeout: TIMEOUT_MS });
    return { status: "unsubscribed" };
  } catch (e) {
    throw toApiError(e, { token: true });
  }
}

/** Send `frequency` and/or `pauseDays` (integer 1-90; 0 or null resumes). The token travels in the JSON body. */
export async function updateNewsletterPreferences({ token, frequency, pauseDays, offers }) {
  const body = { token };
  if (frequency) body.frequency = frequency;
  if (pauseDays !== undefined) body.pause_days = pauseDays;
  if (offers === true || offers === false) body.offers = offers;   // the per-purpose switch; false keeps the newsletter
  try {
    const r = await api.patch("/newsletter/preferences", body, { timeout: TIMEOUT_MS });
    return r.data || {};
  } catch (e) {
    throw toApiError(e, { token: true });
  }
}
